import { useCallback, useEffect, useId, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation, useNavigate } from 'react-router';
import { useIsFetching } from '@tanstack/react-query';
import { MEALS, addDays, addMonths, daysLabel, diffDays, formatDay, formatMonth, isIsoDate, isoWeekday, mealsOf, monthOf, sameDays, slotOf, todayIn, type CalendarCell, type Food, type Meal, type Slot } from '@tiffinify/core';

/* ---------------------------------------------------------------- icons */
const ICONS = {
  back: <path d="m15 18-6-6 6-6" />,
  close: <path d="M18 6 6 18M6 6l12 12" />,
  plus: <path d="M12 5v14M5 12h14" />,
  search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>,
  right: <path d="m9 6 6 6-6 6" />,
  left: <path d="m15 6-6 6 6 6" />,
  down: <path d="m6 9 6 6 6-6" />,
  up: <path d="m6 15 6-6 6 6" />,
  chat: <path d="M20.5 11.5a8.5 8.5 0 0 1-12.6 7.4L3.5 20l1.2-4.2a8.5 8.5 0 1 1 15.8-4.3z" />,
  link: <><path d="M10 13a5 5 0 0 0 7.07 0l3-3A5 5 0 0 0 13 2.93l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.07 0l-3 3A5 5 0 0 0 11 21.07l1.71-1.71" /></>,
  pause: <path d="M9 5v14M15 5v14" />,
  swap: <><path d="M4 8h13l-3.5-3.5" /><path d="M20 16H7l3.5 3.5" /></>,
  rupee: <path d="M6 4h12M6 8.5h12M9 4c3.5 0 5.5 1.8 5.5 4.5S12.5 13 9 13H7.5l7 7" />,
  calendar: <><rect x="3.5" y="5" width="17" height="15.5" rx="2.5" /><path d="M3.5 10h17M8 3v4M16 3v4" /></>,
  check: <path d="m5 12.5 4.5 4.5L19 7.5" />,
  arrow: <path d="M5 12h14M13 6l6 6-6 6" />,
  send: <><path d="M21 3 10 14" /><path d="m21 3-6.5 18-4-7.5L3 9.5z" /></>,
  camera: <><path d="M4 8.5A2.5 2.5 0 0 1 6.5 6h1.8l1.5-2h4.4l1.5 2h1.8A2.5 2.5 0 0 1 20 8.5v9a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 17.5z" /><circle cx="12" cy="13" r="3.5" /></>,
  sheet: <><path d="M14 3.5H7A2.5 2.5 0 0 0 4.5 6v12A2.5 2.5 0 0 0 7 20.5h10a2.5 2.5 0 0 0 2.5-2.5V9z" /><path d="M14 3.5V9h5.5M8.5 13h7M8.5 16.5h7" /></>,
  pin: <><path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11z" /><circle cx="12" cy="10" r="2.3" /></>,
  refresh: <><path d="M20 11a8 8 0 0 0-14.3-4.9L4 8" /><path d="M4 3.5V8h4.5" /><path d="M4 13a8 8 0 0 0 14.3 4.9L20 16" /><path d="M20 20.5V16h-4.5" /></>,
  home: <><path d="M3.5 10.5 12 3.5l8.5 7" /><path d="M5.5 9v11h13V9" /></>,
  people: <><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20c.8-3.6 3.3-5.5 6.5-5.5s5.7 1.9 6.5 5.5" /><path d="M15.5 4.7a3.5 3.5 0 0 1 0 6.6" /><path d="M17.5 14.8c1.9.8 3.2 2.5 3.9 5.2" /></>,
  pot: <><path d="M4 10.5h16v3.5a6 6 0 0 1-6 6h-4a6 6 0 0 1-6-6z" /><path d="M2 10.5h20" /><path d="M9.5 3.5c-.8.9-.8 2.1 0 3M14.5 3.5c-.8.9-.8 2.1 0 3" /></>,
  edit: <path d="M4 20h4L19 9a2.8 2.8 0 0 0-4-4L4 16z" />,
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 7.6v.4" /></>,
  sliders: <><path d="M4 7h9M17 7h3M4 17h3M11 17h9" /><circle cx="15" cy="7" r="2.2" /><circle cx="9" cy="17" r="2.2" /></>,
  trash: <><path d="M4 7h16M10 11v6M14 11v6" /><path d="M6 7l1 12.5A1.5 1.5 0 0 0 8.5 21h7a1.5 1.5 0 0 0 1.5-1.5L18 7M9 7V4.5h6V7" /></>,
  phone: <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />,
  bike: <><circle cx="5.5" cy="17.5" r="3.5" /><circle cx="18.5" cy="17.5" r="3.5" /><circle cx="15" cy="5" r="1" /><path d="M12 17.5V14l-3-3 4-3 2 3h2" /></>,
  box: <><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="m3.3 7 8.7 5 8.7-5M12 22V12" /></>,
  sparkle: <path d="M12 2l2.4 6.2L20.5 10.5l-6.1 2.3L12 19l-2.4-6.2L3.5 10.5l6.1-2.3z" />,
} as const;

export type IconName = keyof typeof ICONS;

export function Icon({ name, size = 20, sw = 2, color = 'currentColor', style }: { name: IconName; size?: number; sw?: number; color?: string; style?: CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none', ...style }}>
      {ICONS[name]}
    </svg>
  );
}

export function Sun({ size = 13 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#F2B544" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true" style={{ flex: 'none' }}>
      <circle cx="12" cy="12" r="5" fill="#F2B544" />
      <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
    </svg>
  );
}

/** Breakfast: a sun rising over the horizon. */
export function Sunrise({ size = 13 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#E8955A" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true" style={{ flex: 'none' }}>
      <path d="M6.5 17a5.5 5.5 0 0 1 11 0z" fill="#E8955A" />
      <path d="M2 21h20M12 3v3M4.6 9.6l1.6 1.6M19.4 9.6l-1.6 1.6" />
    </svg>
  );
}

/** The icon for one meal. */
export function MealIcon({ meal, size = 13 }: { meal: Meal; size?: number }) {
  return meal === 'breakfast' ? <Sunrise size={size} /> : meal === 'lunch' ? <Sun size={size} /> : <Moon size={size} />;
}

/**
 * Pick one or more meals (breakfast / lunch / dinner). The last selected meal can't be
 * switched off, so the value is always a valid slot. `allowed` limits the choice
 * (e.g. the meals the kitchen serves, or the meals on a plan).
 */
export function MealPicker({ value, onChange, allowed = MEALS, label }: { value: Slot; onChange: (s: Slot) => void; allowed?: readonly Meal[]; label: string }) {
  const on = mealsOf(value);
  const toggle = (m: Meal) => {
    const next = on.includes(m) ? on.filter((x) => x !== m) : [...on, m];
    if (next.length) onChange(slotOf(next));
  };
  return (
    <div role="group" aria-label={label} className="meal-pick">
      {MEALS.filter((m) => allowed.includes(m) || on.includes(m)).map((m) => (
        <button key={m} type="button" aria-pressed={on.includes(m)} onClick={() => toggle(m)}>
          <MealIcon meal={m} size={16} />
          {m.charAt(0).toUpperCase() + m.slice(1)}
        </button>
      ))}
    </div>
  );
}

const WEEK = [['M', 'Monday'], ['T', 'Tuesday'], ['W', 'Wednesday'], ['T', 'Thursday'], ['F', 'Friday'], ['S', 'Saturday'], ['S', 'Sunday']] as const;
const DAY_PRESETS: Array<[string, number[]]> = [['Mon–Sat', [1, 2, 3, 4, 5, 6]], ['Mon–Fri', [1, 2, 3, 4, 5]], ['Every day', [1, 2, 3, 4, 5, 6, 7]]];

/**
 * Pick any combination of weekdays (1 = Monday ... 7 = Sunday): Mon/Tue/Wed, Sundays only, every day.
 * The last day can't be switched off, so there is always at least one. Quick picks for the usual sets.
 */
export function DayPicker({ value, onChange, label, allowed }: { value: readonly number[]; onChange: (days: number[]) => void; label: string; allowed?: readonly number[] }) {
  const on = new Set(value);
  const available = new Set(allowed ?? WEEK.map((_, i) => i + 1));
  const toggle = (d: number) => {
    if (!available.has(d)) return;
    const next = new Set(on);
    if (next.has(d)) next.delete(d); else next.add(d);
    if (next.size) onChange([...next].sort((a, b) => a - b));
  };
  const isPreset = DAY_PRESETS.some(([, days]) => sameDays(value, days));
  return (
    <div className="stack" style={{ gap: 10 }}>
      <div role="group" aria-label={label} className="day-btns">
        {WEEK.map(([letter, name], i) => available.has(i + 1) ? (
          <button key={name} type="button" className="day-btn" aria-pressed={on.has(i + 1)} aria-label={name} onClick={() => toggle(i + 1)}>{letter}</button>
        ) : null)}
      </div>
      <div className="day-presets">
        {DAY_PRESETS.filter(([, days]) => days.every((day) => available.has(day))).map(([name, days]) => (
          <button key={name} type="button" className="day-preset" aria-pressed={sameDays(value, days)} onClick={() => onChange(days)}>{name}</button>
        ))}
        {!isPreset && value.length > 0 && (
          <span className="day-summary" aria-live="polite">{daysLabel(value)}</span>
        )}
      </div>
    </div>
  );
}

export function Moon({ size = 13 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ flex: 'none' }}>
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" fill="#D2C7B8" />
    </svg>
  );
}

export function VegMark({ plate }: { plate?: boolean }) {
  const inner = (
    <span role="img" aria-label="Veg" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 14, height: 14, border: '1.5px solid #2F7D32', borderRadius: 3 }}>
      <span style={{ width: 6, height: 6, borderRadius: 3, background: '#2F7D32' }} />
    </span>
  );
  return plate ? <span className="mark-plate">{inner}</span> : <span style={{ display: 'flex', flex: 'none' }}>{inner}</span>;
}

export function NonVegMark({ plate }: { plate?: boolean }) {
  const inner = (
    <span role="img" aria-label="Non-veg" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 14, height: 14, border: '1.5px solid #8A3B12', borderRadius: 3 }}>
      <svg width="8" height="7" viewBox="0 0 8 7" aria-hidden="true"><path d="M4 0 8 7H0z" fill="#8A3B12" /></svg>
    </span>
  );
  return plate ? <span className="mark-plate">{inner}</span> : <span style={{ display: 'flex', flex: 'none' }}>{inner}</span>;
}

export function EggMark({ plate }: { plate?: boolean }) {
  const inner = (
    <span role="img" aria-label="Egg" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 14, height: 14, border: '1.5px solid #B07A1E', borderRadius: 3, flex: 'none' }}>
      <span style={{ width: 6, height: 6, borderRadius: 3, background: '#F2B544' }} />
    </span>
  );
  return plate ? <span className="mark-plate">{inner}</span> : inner;
}

export function FoodMark({ food, plate }: { food: 'veg' | 'nonveg' | 'egg'; plate?: boolean }) {
  return food === 'veg' ? <VegMark plate={plate} /> : food === 'nonveg' ? <NonVegMark plate={plate} /> : <EggMark plate={plate} />;
}

const DIETS: Array<{ value: Food; label: ReactNode }> = [
  { value: 'veg', label: <span className="diet-option"><VegMark /> Veg</span> },
  { value: 'nonveg', label: <span className="diet-option"><NonVegMark /> Non-veg</span> },
  { value: 'egg', label: <span className="diet-option"><EggMark /> Egg</span> },
];

/** Veg, non-veg or egg, with their marks. */
export const DietPicker = ({ value, onChange, label }: { value: Food; onChange: (f: Food) => void; label: string }) => (
  <Segmented label={label} value={value} onChange={onChange} options={DIETS} />
);

const FOOD_NAME: Record<Food, string> = { veg: 'Veg', nonveg: 'Non-veg', egg: 'Egg' };

/** Filter a list by diet: All, then each diet that has something, with counts. */
export function DietChips({ items, value, onChange }: { items: ReadonlyArray<{ food: Food }>; value: Food | 'all'; onChange: (f: Food | 'all') => void }) {
  const count = (f: Food) => items.filter((x) => x.food === f).length;
  return (
    <Chips sm scroll label="Diet">
      <Chip on={value === 'all'} count={items.length} onClick={() => onChange('all')}>All</Chip>
      {(['veg', 'nonveg', 'egg'] as const).filter((f) => count(f) > 0 || value === f).map((f) => (
        <Chip key={f} on={value === f} count={count(f)} onClick={() => onChange(f)}>{FOOD_NAME[f]}</Chip>
      ))}
    </Chips>
  );
}

/* ---------------------------------------------------------------- brand */
export function Mark({ size = 28, dark = false }: { size?: number; dark?: boolean }) {
  const tier = dark ? '#F5EFE6' : '#17140F';
  const ring = dark ? '#1C1813' : '#F6F3EE';
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ flex: 'none', display: 'block' }}>
      <path d="M9.4 5.4V4Q9.4 2.7 10.7 2.7h2.6q1.3 0 1.3 1.3v1.4" fill="none" stroke={tier} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="3" y="5.4" width="18" height="5" rx="2.5" fill={tier} />
      <rect x="3" y="11.1" width="18" height="5" rx="2.5" fill={tier} />
      <rect x="3" y="16.8" width="18" height="5" rx="2.5" fill={tier} />
      <circle cx="19.8" cy="5.8" r="2.6" fill="#F2B544" stroke={ring} strokeWidth="1.7" />
    </svg>
  );
}

export function Wordmark({ size = 24, dark = false }: { size?: number; dark?: boolean }) {
  return (
    <span role="img" aria-label="tiffinify" className={`wordmark${dark ? ' dark' : ''}`} style={{ fontSize: size, color: dark ? '#F5EFE6' : '#17140F' }}>
      t<span className="i">ı</span>ff<span className="i">ı</span>n<span className="i">ı</span>fy
    </span>
  );
}

export function Logo({ size = 18, dark = false }: { size?: number; dark?: boolean }) {
  return (
    <div className="logo" style={{ gap: Math.round(size * 0.45) }}>
      <Mark size={Math.round(size * 1.45)} dark={dark} />
      <Wordmark size={Math.round(size * 1.25)} dark={dark} />
    </div>
  );
}

/* --------------------------------------------------------------- loader */
/** The logo, animated as a loader: tiers drop in, the handle latches, the dot pops, a shine sweeps. Styles in loader.css. */
export function TiffinSpinner({ size = 64 }: { size?: number }) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const clip = `tl-clip-${uid}`;
  const shine = `tl-shine-${uid}`;
  return (
    <svg className="tl" width={size} height={size} viewBox="-4 -4 32 32" aria-hidden="true">
      <defs>
        <clipPath id={clip}>
          <rect x="3" y="5.4" width="18" height="5" rx="2.5" />
          <rect x="3" y="11.1" width="18" height="5" rx="2.5" />
          <rect x="3" y="16.8" width="18" height="5" rx="2.5" />
        </clipPath>
        <linearGradient id={shine} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#fff" stopOpacity="0" />
          <stop offset=".5" stopColor="#fff" stopOpacity=".55" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
      </defs>
      <ellipse className="tl-shadow" cx="12" cy="24.2" rx="8.5" ry="1" />
      <g className="tl-pack">
      <rect className="tl-tier tl-t3" x="3" y="16.8" width="18" height="5" rx="2.5" />
      <rect className="tl-tier tl-t2" x="3" y="11.1" width="18" height="5" rx="2.5" />
      <rect className="tl-tier tl-t1" x="3" y="5.4" width="18" height="5" rx="2.5" />
      <g className="tl-shine-g" clipPath={`url(#${clip})`}>
        <g transform="skewX(-20)">
          <rect className="tl-shine" x="-8" y="4" width="6" height="19" fill={`url(#${shine})`} />
        </g>
      </g>
      <path className="tl-handle" pathLength={1} d="M9.4 5.4V4Q9.4 2.7 10.7 2.7h2.6q1.3 0 1.3 1.3v1.4" />
      <circle className="tl-ripple" cx="19.8" cy="5.8" r="2.6" />
      <circle className="tl-dot" cx="19.8" cy="5.8" r="2.6" />
      </g>
    </svg>
  );
}

/** Full-area loading state: just the animated logo. `label` is read to screen readers only. */
export function PageLoader({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="loader" role="status" aria-label={label}>
      <TiffinSpinner size={72} />
    </div>
  );
}

/** Background refreshes (after a save, on refocus). Hidden while a full PageLoader is on screen. */
export function SyncPill() {
  const n = useIsFetching(); // saves already show the busy mark on their button
  if (n === 0) return null;
  return (
    <div className="sync-pill" role="status" aria-label="Syncing">
      <TiffinMini />
    </div>
  );
}

/** Small busy mark used inside buttons while a save is in flight. */
export const TiffinMini = () => (
  <span className="tl-mini" aria-hidden="true"><i /><i /><i /></span>
);

/** Stand-in for a line of text that is still loading. Same height as the text, so nothing shifts when it arrives. */
export const Skel = ({ w = 140 }: { w?: number }) => (
  <span className="skel" style={{ '--w': `${w}px` } as CSSProperties} aria-hidden="true" />
);

export function KitchenChip({ name }: { name: string }) {
  return (
    <Link to="/settings" className="kitchen-chip" aria-label={`${name} kitchen rules`}>
      <i aria-hidden="true">{name.slice(0, 1).toUpperCase()}</i>
      <span>{name}</span>
      <Icon name="sliders" size={16} sw={2.1} color="var(--ink-2)" />
    </Link>
  );
}

/** Top bar showing brand logo on the left and kitchen chip or custom action on the right. */
export function AppHeader({ kitchenName, right }: { kitchenName?: string; right?: ReactNode }) {
  return (
    <header className="between" style={{ minHeight: 44 }}>
      <Logo />
      {right !== undefined ? right : (kitchenName ? <KitchenChip name={kitchenName} /> : null)}
    </header>
  );
}

/** Renders subtitle with signature brand saffron dot on middle dots ' · ' */
export function brandText(node: ReactNode): ReactNode {
  if (typeof node !== 'string') return node;
  if (!node.includes(' · ')) return node;

  const parts = node.split(/( · )/);
  return (
    <>
      {parts.map((p, idx) => (p === ' · ' ? <span key={idx} className="brand-sep-dot"> · </span> : p))}
    </>
  );
}

/** Renders title with brand tiffinify style: lowercase with signature saffron dot on 'i' and 'j' */
export function brandTitle(node: ReactNode): ReactNode {
  if (typeof node !== 'string') return node;

  const lower = node.toLowerCase();
  const parts = lower.split(/([ij])/);

  return (
    <span className="brand-title" aria-label={node}>
      {parts.map((p, idx) => {
        if (p === 'i') return <span key={idx} className="i" aria-hidden="true">ı</span>;
        if (p === 'j') return <span key={idx} className="i" aria-hidden="true">ȷ</span>;
        return <span key={idx} aria-hidden="true">{p}</span>;
      })}
    </span>
  );
}

/* ---------------------------------------------------------------- text */
export const Mono = ({ children, muted, saffron }: { children: ReactNode; muted?: boolean; saffron?: boolean }) => (
  <span className={`mono${muted ? ' muted' : ''}${saffron ? ' saffron' : ''}`}>{children}</span>
);

export function PageTitle({ title, sub, action }: { title: ReactNode; sub?: ReactNode; action?: ReactNode }) {
  return (
    <header className="page-title">
      <div>
        <h1>{brandTitle(title)}</h1>
        {sub && <span className="sub">{brandText(sub)}</span>}
      </div>
      {action}
    </header>
  );
}

export function TaskTitle({ title, sub }: { title: string; sub?: ReactNode }) {
  return (
    <div className="task-title">
      <h1>{brandTitle(title)}</h1>
      {sub && <span className="sub">{brandText(sub)}</span>}
    </div>
  );
}

export function Section({ title, meta, children }: { title: string; meta?: ReactNode; children: ReactNode }) {
  return (
    <section className="section" aria-label={title}>
      <header>
        <h2>{brandTitle(title)}</h2>
        {meta}
      </header>
      {children}
    </section>
  );
}

/** A note in a form about what is about to happen, before it does: switching meals, closing a plan part-way. */
export const Callout = ({ icon = 'info', title, children }: { icon?: IconName; title: ReactNode; children?: ReactNode }) => (
  <div className="callout">
    <Icon name={icon} size={18} />
    <div className="grow"><b>{title}</b>{children && <p>{children}</p>}</div>
  </div>
);

export const Helper = ({ children, icon }: { children: ReactNode; icon?: IconName }) => (
  <p className="helper">
    {icon && <Icon name={icon} size={16} color="var(--ink-2)" />}
    <span>{children}</span>
  </p>
);

export const ErrorText = ({ children }: { children?: ReactNode }) => (children ? <p className="err" role="alert">{children}</p> : null);

/* ---------------------------------------------------------------- containers */
export const Hero = ({ children, label, gap }: { children: ReactNode; label: string; gap?: number }) => (
  <section className="hero" aria-label={label} style={gap ? { gap } : undefined}>{children}</section>
);

export const Card = ({ children, pad, className = '' }: { children: ReactNode; pad?: boolean | 'sm'; className?: string }) => (
  <div className={`card${pad === true ? ' pad' : pad === 'sm' ? ' pad-sm' : ''} ${className}`}>{children}</div>
);

export const CardTitle = ({ title, right }: { title: string; right?: ReactNode }) => (
  <div className="card-title">
    <h3>{brandTitle(title)}</h3>
    {right}
  </div>
);

/** A card that folds away: a heading row (icon, title, what is in it) and, when open, its rows. */
export function FoldCard({ icon, title, meta, open, onToggle, children }: {
  icon: ReactNode; title: string; meta: ReactNode; open: boolean; onToggle: () => void; children: ReactNode;
}) {
  return (
    <Card>
      <button type="button" className={`group-head${open ? ' open' : ''}`} onClick={onToggle} aria-expanded={open}>
        <span className="group-head-icon">{icon}</span>
        <span className="group-head-title">{title}</span>
        {meta}
        <span className="group-head-chevron"><Icon name="down" size={16} /></span>
      </button>
      {open && children}
    </Card>
  );
}

/**
 * The one dialog: a sheet from the bottom of the screen (`center` for small pickers). Escape, the backdrop and the
 * close button all close it, and the page behind stops scrolling while it is open.
 */
export function Sheet({ title, kicker, sub, onClose, center, className = '', children }: {
  title: ReactNode; kicker?: ReactNode; sub?: ReactNode; onClose: () => void; center?: boolean; className?: string; children: ReactNode;
}) {
  const titleId = useId();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = overflow; };
  }, [onClose]);
  // In the body, not where it was opened: nothing it sits inside (a hero, a centred field) can restyle it.
  return createPortal(
    <div className={`sheet-scrim${center ? ' center' : ''}`}>
      <button type="button" className="sheet-backdrop" aria-label="Close" tabIndex={-1} onClick={onClose} />
      <div className={`sheet ${className}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="sheet-header">
          <div className="sheet-header-text">
            {kicker && <span className="sheet-badge">{kicker}</span>}
            <h3 id={titleId}>{title}</h3>
            {sub && <span className="sheet-sub">{sub}</span>}
          </div>
          <IconBtn icon="close" label="Close" ghost onClick={onClose} />
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

interface ConfirmAsk { title: string; body?: string; yes: string; danger?: boolean }

/** Asks before an action that cannot be undone, in the app's own popup: `if (await confirm({...})) act();`. Render `sheet`. */
export function useConfirm(): { confirm: (ask: ConfirmAsk) => Promise<boolean>; sheet: ReactNode } {
  const [ask, setAsk] = useState<(ConfirmAsk & { answer: (ok: boolean) => void }) | null>(null);
  const confirm = useCallback((a: ConfirmAsk) => new Promise<boolean>((answer) => setAsk({ ...a, answer })), []);
  const reply = (ok: boolean) => { ask?.answer(ok); setAsk(null); };
  const sheet = ask && (
    <Sheet center className="confirm" kicker={ask.danger ? 'This can’t be undone' : 'Please confirm'} title={ask.title} onClose={() => reply(false)}>
      {ask.body && <p className="confirm-body">{ask.body}</p>}
      <div className="confirm-actions">
        <BtnSecondary label="Cancel" onClick={() => reply(false)} />
        <BtnSecondary label={ask.yes} icon={ask.danger ? 'trash' : 'check'} danger={ask.danger} ink={!ask.danger} onClick={() => reply(true)} />
      </div>
    </Sheet>
  );
  return { confirm, sheet };
}

export const KV = ({ k, v }: { k: ReactNode; v: ReactNode }) => (
  <div className="kv">
    <span>{k}</span>
    <span>{v}</span>
  </div>
);

export function Badge({ kind = 'neutral', icon, children }: { kind?: 'end' | 'due' | 'ok' | 'neutral' | 'saffron'; icon?: IconName; children: ReactNode }) {
  return (
    <span className={`badge ${kind === 'neutral' ? '' : kind}`}>
      {icon && <Icon name={icon} size={12} sw={2.6} />}
      {children}
    </span>
  );
}

/* ---------------------------------------------------------------- avatars */
const RING_C = 2 * Math.PI * 20.5;

export function RingAvatar({ name, frac, color }: { name: string; frac: number; color: string }) {
  const dash = `${Math.max(0, Math.min(1, frac)) * RING_C} ${RING_C}`;
  return (
    <span className="ring" aria-hidden="true">
      <svg width="44" height="44" viewBox="0 0 44 44">
        <circle cx="22" cy="22" r="20.5" fill="none" stroke="#ECE6DC" strokeWidth="2" />
        <circle cx="22" cy="22" r="20.5" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeDasharray={dash} transform="rotate(-90 22 22)" />
      </svg>
      <span>{initials(name)}</span>
    </span>
  );
}

export function Avatar({ name, size = 40, ink }: { name: string; size?: number; ink?: boolean }) {
  return (
    <span className={`avatar${ink ? ' ink' : ''}`} aria-hidden="true" style={{ width: size, height: size }}>
      {initials(name)}
    </span>
  );
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '')).toUpperCase();
}

/* ---------------------------------------------------------------- buttons */
export function IconBtn({ icon, label, to, onClick, ghost, href, disabled }: { icon: IconName; label: string; to?: string; onClick?: () => void; ghost?: boolean; href?: string; disabled?: boolean }) {
  const cls = `icon-btn${ghost ? ' ghost' : ''}`;
  const inner = <Icon name={icon} size={19} sw={2.1} />;
  if (to && !disabled) return <Link to={to} className={cls} aria-label={label}>{inner}</Link>;
  if (href && !disabled) return <a href={href} target="_blank" rel="noreferrer" className={cls} aria-label={label}>{inner}</a>;
  return <button type="button" className={cls} aria-label={label} onClick={onClick} disabled={disabled}>{inner}</button>;
}

interface BtnProps { label: string; icon?: IconName; to?: string; href?: string; onClick?: () => void; disabled?: boolean; type?: 'button' | 'submit'; /** Shows the tiffin busy mark and disables the button. */ busy?: boolean }

export function BtnPrimary({ label, icon = 'arrow', to, href, onClick, disabled, type = 'button', busy }: BtnProps) {
  const inner = <>{label}<span aria-hidden="true">{busy ? <TiffinMini /> : <Icon name={icon} size={18} sw={2.4} />}</span></>;
  if (to && !disabled) return <Link to={to} className="btn-primary">{inner}</Link>;
  if (href && !disabled) return <a href={href} target="_blank" rel="noreferrer" className="btn-primary">{inner}</a>;
  return <button type={type} className={`btn-primary${busy ? ' busy' : ''}`} onClick={onClick} disabled={disabled || busy} aria-busy={busy || undefined}>{inner}</button>;
}

export function BtnSecondary({ label, icon, to, href, onClick, disabled, sm, danger, ink, busy }: BtnProps & { sm?: boolean; danger?: boolean; /** Dark, for the one action that matters most inside a card. */ ink?: boolean }) {
  const cls = `btn-secondary${sm ? ' sm' : ''}${danger ? ' btn-danger' : ''}${ink ? ' ink' : ''}${busy ? ' busy' : ''}`;
  const inner = <>{busy ? <TiffinMini /> : icon && <Icon name={icon} size={18} sw={2.1} />}{label}</>;
  if (to) return <Link to={to} className={cls}>{inner}</Link>;
  if (href) return <a href={href} target="_blank" rel="noreferrer" className={cls}>{inner}</a>;
  return <button type="button" className={cls} onClick={onClick} disabled={disabled || busy} aria-busy={busy || undefined}>{inner}</button>;
}

/** A row of round shortcuts with a label under each (the customer's plan actions). */
export const QuickActions = ({ children, label }: { children: ReactNode; label: string }) => (
  <nav className="quick-actions" aria-label={label}>{children}</nav>
);

export function QuickAction({ label, icon, to }: { label: string; icon: IconName; to: string }) {
  return (
    <Link to={to} className="quick-action">
      <span aria-hidden="true"><Icon name={icon} size={21} sw={2.2} /></span>
      <b>{label}</b>
    </Link>
  );
}

export function PillAction({ label, to, icon = 'plus', onClick }: { label: string; to?: string; icon?: IconName; onClick?: () => void }) {
  const inner = <><Icon name={icon} size={18} sw={2.4} />{label}</>;
  return to ? <Link to={to} className="pill-action">{inner}</Link> : <button type="button" className="pill-action" onClick={onClick}>{inner}</button>;
}

export function TextLink({ label, to, onClick }: { label: string; to?: string; onClick?: () => void }) {
  const inner = <>{label}<Icon name="right" size={16} sw={2.4} /></>;
  return to ? <Link to={to} className="text-link">{inner}</Link> : <button type="button" className="text-link" onClick={onClick}>{inner}</button>;
}

/** Back / close row at the top of task screens. */
export function TopBar({ to, label = 'Back', kind = 'back', right, logo = true }: { to?: string; label?: string; kind?: 'back' | 'close'; right?: ReactNode; logo?: boolean }) {
  const nav = useNavigate();
  return (
    <div className="between" style={{ minHeight: 44 }}>
      {to ? <IconBtn icon={kind} label={label} to={to} /> : <IconBtn icon={kind} label={label} onClick={() => nav(-1)} />}
      {logo && <Logo size={16} />}
      {right ? <div className="row">{right}</div> : logo ? <div style={{ width: 44 }} aria-hidden="true" /> : <div />}
    </div>
  );
}

/* ---------------------------------------------------------------- controls */
export function Segmented<T extends string>({ options, value, onChange, label }: { options: Array<{ value: T; label: ReactNode }>; value: T; onChange: (v: T) => void; label: string }) {
  return (
    <div role="group" aria-label={label} className="seg">
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

/** `count={null}` holds the count's place while it loads; `fixed` keeps the width when the label can change. */
export function Chip({ children, on, onClick, count, icon, dashed, fixed }: { children: ReactNode; on?: boolean; onClick?: () => void; count?: number | null; icon?: IconName; dashed?: boolean; fixed?: boolean }) {
  return (
    <button type="button" className={`chip${dashed ? ' dashed' : ''}${fixed ? ' fixed' : ''}`} aria-pressed={!!on} onClick={onClick}>
      {icon && <Icon name={icon} size={16} sw={2.3} />}
      <span className="label-text">{children}</span>
      {count === null ? <Skel w={12} /> : count !== undefined && <small>{count}</small>}
    </button>
  );
}

export const Chips = ({ children, label, scroll, sm }: { children: ReactNode; label: string; scroll?: boolean; sm?: boolean }) => (
  <div role="group" aria-label={label} className={`chips ${scroll ? 'scroll' : 'wrap'}${sm ? ' sm' : ''}`}>{children}</div>
);

/** The search box, everywhere: magnifier, field, and a clear button once there is text. */
export function SearchField({ value, onChange, label, placeholder }: { value: string; onChange: (v: string) => void; label: string; placeholder: string }) {
  return (
    <label className="search">
      <Icon name="search" size={18} sw={2.2} />
      <span className="sr">{label}</span>
      <input type="search" placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
      {value && (
        <button type="button" className="search-clear" aria-label="Clear search" onClick={() => onChange('')}>
          <Icon name="close" size={13} sw={2.4} />
        </button>
      )}
    </label>
  );
}

export interface FieldProps {
  label: string;
  id: string;
  value: string;
  onChange?: (v: string) => void;
  type?: string;
  placeholder?: string;
  prefix?: string;
  suffix?: string;
  hint?: ReactNode;
  error?: string | undefined;
  inputMode?: 'numeric' | 'tel' | 'text' | 'decimal';
  autoComplete?: string;
  readOnly?: boolean;
  min?: string;
  max?: string;
  maxLength?: number;
  today?: string;
  tomorrow?: string;
}

export interface DateFieldProps {
  label: string;
  id: string;
  value?: string;
  onChange?: (val: string) => void;
  placeholder?: string;
  hint?: ReactNode;
  error?: string | undefined;
  readOnly?: boolean;
  min?: string;
  max?: string;
  today?: string;
  tomorrow?: string;
}

export function DateField({
  label,
  id,
  value,
  onChange,
  placeholder,
  hint,
  error,
  readOnly,
  min,
  max,
  today: propToday,
  tomorrow: propTomorrow,
}: DateFieldProps) {
  const [open, setOpen] = useState(false);
  const today = propToday ?? todayIn();
  const tomorrow = propTomorrow ?? addDays(today, 1);
  const isToday = value ? value === today : false;
  const isTomorrow = value ? value === tomorrow : false;

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <button
        type="button"
        id={id}
        className={`box date-field-box${error ? ' invalid' : ''}${readOnly ? ' readonly' : ''}`}
        onClick={() => !readOnly && setOpen(true)}
        disabled={readOnly}
        aria-invalid={!!error}
        aria-haspopup="dialog"
      >
        <span className="date-field-content">
          {value && isIsoDate(value) ? (
            <span className="date-field-val">
              <span className="date-field-main">{formatDay(value)}</span>
              <span className="date-field-sub">{value.slice(0, 4)}</span>
              {isToday && <span className="date-field-badge">Today</span>}
              {isTomorrow && <span className="date-field-badge tomorrow">Tomorrow</span>}
            </span>
          ) : (
            <span className="date-field-placeholder">{placeholder ?? 'Pick a date'}</span>
          )}
        </span>
        <Icon name="calendar" size={18} color="var(--ink-2)" />
      </button>
      {error ? <span className="hint bad">{error}</span> : hint ? <span className="hint">{hint}</span> : null}

      <DatePickerSheet
        isOpen={open}
        onClose={() => setOpen(false)}
        value={value ?? ''}
        onChange={(d) => onChange?.(d)}
        today={today}
        min={min}
        max={max}
        title={label}
      />
    </div>
  );
}

export function Field(props: FieldProps) {
  const { label, id, value, onChange, type = 'text', placeholder, prefix, suffix, hint, error, inputMode, autoComplete, readOnly, min, max, maxLength, today, tomorrow } = props;

  if (type === 'date') {
    return (
      <DateField
        label={label}
        id={id}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        hint={hint}
        error={error}
        readOnly={readOnly}
        min={min}
        max={max}
        today={today}
        tomorrow={tomorrow}
      />
    );
  }

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className={`box${error ? ' invalid' : ''}`}>
        {prefix && <span className="prefix">{prefix}</span>}
        <input id={id} type={type} value={value} placeholder={placeholder} inputMode={inputMode} autoComplete={autoComplete} readOnly={readOnly} min={min} max={max} maxLength={maxLength} aria-invalid={!!error} onChange={(e) => onChange?.(e.target.value)} />
        {suffix && <span className="suffix">{suffix}</span>}
      </div>
      {error ? <span className="hint bad">{error}</span> : hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
}

export function ToggleRow({ title, sub, on, onChange }: { title: string; sub?: string; on: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="toggle-row">
      <div className="grow">
        <b>{title}</b>
        {sub && <small>{sub}</small>}
      </div>
      <button type="button" role="switch" aria-checked={on} aria-label={title} className="switch" onClick={() => onChange(!on)}><i /></button>
    </div>
  );
}

export function RadioCard({ title, sub, on, right, onClick }: { title: string; sub: string; on: boolean; right?: string; onClick: () => void }) {
  return (
    <button type="button" className="radio-card" aria-pressed={on} onClick={onClick}>
      <span className="dot" aria-hidden="true">{on && <Icon name="check" size={14} sw={3} />}</span>
      <span className="grow"><b>{title}</b><small>{sub}</small></span>
      {right && <span className="right">{right}</span>}
    </button>
  );
}

export const Steps = ({ n, total = 3 }: { n: number; total?: number }) => (
  <div className="steps" aria-hidden="true">{Array.from({ length: total }, (_, i) => <i key={i} className={i < n ? 'on' : ''} />)}</div>
);

/** How much of something is done, on a light card: saffron while partway, leaf once complete. */
export const Meter = ({ value, total }: { value: number; total: number }) => {
  const frac = total > 0 ? Math.max(0, Math.min(1, value / total)) : 0;
  return <div className={`meter${frac >= 1 ? ' full' : ''}`} aria-hidden="true"><i style={{ width: `${frac * 100}%` }} /></div>;
};

export const SegBar = ({ done, total }: { done: number; total: number }) => (
  <div className="seg-bar" aria-hidden="true">{Array.from({ length: total }, (_, i) => <i key={i} className={i < done ? 'on' : ''} />)}</div>
);

export const Banner = ({ children, action }: { children: ReactNode; action?: ReactNode }) => (
  <div className="banner" role="status"><span className="grow">{children}</span>{action}</div>
);

export const Empty = ({ title, children }: { title: string; children?: ReactNode }) => (
  <div className="empty"><strong>{title}</strong>{children}</div>
);

export function EmptyFilterState({
  title = 'No dishes match your filter',
  desc,
  onReset,
  resetLabel = 'Reset filters',
  icon = 'search',
}: {
  title?: string;
  desc?: string;
  onReset?: () => void;
  resetLabel?: string;
  icon?: IconName;
}) {
  return (
    <div className="empty-filter-state">
      <div className="empty-filter-icon" aria-hidden="true">
        <Icon name={icon} size={18} sw={2.2} />
      </div>
      <strong className="empty-filter-title">{title}</strong>
      {desc && <p className="empty-filter-desc">{desc}</p>}
      {onReset && (
        <button type="button" className="btn-reset-filters" onClick={onReset}>
          <Icon name="refresh" size={13} sw={2.4} />
          {resetLabel}
        </button>
      )}
    </div>
  );
}

/** A screen's data could not be loaded (offline, server down): say so and offer to try again. */
export function LoadError({ what, onRetry }: { what: string; onRetry: () => void }) {
  return (
    <Card>
      <EmptyFilterState icon="info" title={`Couldn’t load ${what}`} desc="Check your connection, then try again." onReset={onRetry} resetLabel="Try again" />
    </Card>
  );
}

/* ---------------------------------------------------------------- navigation */
const NAV: Array<{ to: string; label: string; icon: IconName }> = [
  { to: '/', label: 'Today', icon: 'home' },
  { to: '/subscribers', label: 'Subscribers', icon: 'people' },
  { to: '/kitchen', label: 'Kitchen', icon: 'pot' },
  { to: '/delivery', label: 'Delivery', icon: 'bike' },
];

/** The bar is fixed to the screen (see .nav-dock); the spacer keeps the end of the page above it. */
export function BottomNav() {
  const location = useLocation();
  const isCurrent = (to: string) => {
    if (to === '/') return location.pathname === '/';
    if (to === '/subscribers') return location.pathname.startsWith('/subscribers') || location.pathname.startsWith('/registrations');
    if (to === '/kitchen') return location.pathname.startsWith('/kitchen') || location.pathname.startsWith('/revenue');
    return location.pathname.startsWith(to);
  };

  return (
    <>
      <div className="nav-space" aria-hidden="true" />
      <div className="nav-dock">
        <nav className="nav" aria-label="Main">
          {NAV.map((n) => {
            const active = isCurrent(n.to);
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === '/'}
                className={({ isActive }) => (active || isActive ? 'active' : undefined)}
                aria-current={active ? 'page' : undefined}
              >
                {({ isActive }) => {
                  const current = active || isActive;
                  return (
                    <>
                      <Icon name={n.icon} size={20} sw={current ? 2.1 : 1.9} />
                      {n.label}
                    </>
                  );
                }}
              </NavLink>
            );
          })}
        </nav>
      </div>
    </>
  );
}

/* ---------------------------------------------------------------- calendar */
export type CalMode = 'view' | 'pick';

const CAL_CLASS: Record<string, string> = {
  none: '', off: 'off', holiday: 'holiday', delivered: 'done', today: 'today', paused: 'paused', partial: 'partial', upcoming: 'upcoming', end: 'end',
};

export function MonthGrid({
  cells, aria, onPick, classFor, disabledFor,
}: {
  cells: CalendarCell[];
  aria: string;
  onPick?: (day: string) => void;
  classFor?: (c: CalendarCell) => string;
  disabledFor?: (c: CalendarCell) => boolean;
}) {
  const first = cells[0];
  const lead = first ? (new Date(`${first.date}T00:00:00Z`).getUTCDay() + 6) % 7 : 0;
  return (
    <div role={onPick ? 'group' : 'img'} aria-label={aria} className="cal-grid">
      {'MTWTFSS'.split('').map((d, i) => <span key={i} className="wk">{d}</span>)}
      {Array.from({ length: lead }, (_, i) => <span key={`l${i}`} aria-hidden="true" />)}
      {cells.map((c) => {
        const cls = `cal-day ${classFor ? classFor(c) : CAL_CLASS[c.state] ?? ''}`;
        const n = Number(c.date.slice(8));
        const dis = disabledFor ? disabledFor(c) : true;
        return onPick && !dis ? (
          <button key={c.date} type="button" className={cls} aria-label={c.date} aria-pressed={cls.includes('sel')} onClick={() => onPick(c.date)}>{n}</button>
        ) : (
          <span key={c.date} className={cls} aria-hidden={onPick ? undefined : 'true'}>{n}</span>
        );
      })}
    </div>
  );
}

export function CalHeader({
  title,
  onPrev,
  onNext,
  prevDisabled,
  nextDisabled,
}: {
  title: string;
  onPrev: () => void;
  onNext: () => void;
  prevDisabled?: boolean;
  nextDisabled?: boolean;
}) {
  return (
    <div className="cal-head">
      <h3>{title}</h3>
      <div className="navs">
        <IconBtn icon="left" label="Previous month" ghost onClick={onPrev} disabled={prevDisabled} />
        <IconBtn icon="right" label="Next month" ghost onClick={onNext} disabled={nextDisabled} />
      </div>
    </div>
  );
}

export const Legend = ({ items }: { items: Array<{ cls: string; label: string }> }) => (
  <div className="legend">{items.map((i) => <span key={i.label}><i className={i.cls} />{i.label}</span>)}</div>
);

export function DateNav({
  date,
  onChange,
  today,
  tomorrow,
}: {
  date: string;
  onChange: (d: string) => void;
  today?: string;
  tomorrow?: string;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const isToday = today ? date === today : false;
  const isTomorrow = tomorrow ? date === tomorrow : false;

  return (
    <>
      <div className="date-nav" role="toolbar" aria-label="Date navigation">
        <button
          type="button"
          className="date-nav-btn"
          onClick={() => onChange(addDays(date, -1))}
          aria-label="Previous day"
        >
          <Icon name="left" size={17} />
        </button>

        <button
          type="button"
          className="date-nav-center"
          onClick={() => setPickerOpen(true)}
          aria-label="Open date picker"
        >
          <span className="date-nav-cal-icon">
            <Icon name="calendar" size={15} />
          </span>
          <span className="date-nav-display">
            <span className="date-nav-main">{formatDay(date)}</span>
            <span className="date-nav-sub">· {date.slice(0, 4)}</span>
          </span>
          {isToday && <span className="date-nav-tag today">Today</span>}
          {isTomorrow && <span className="date-nav-tag">Tomorrow</span>}
        </button>

        <button
          type="button"
          className="date-nav-btn"
          onClick={() => onChange(addDays(date, 1))}
          aria-label="Next day"
        >
          <Icon name="right" size={17} />
        </button>
      </div>

      <DatePickerSheet
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        value={date}
        onChange={onChange}
        today={today}
      />
    </>
  );
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAY_LONG = new Intl.DateTimeFormat('en-IN', { weekday: 'long', timeZone: 'UTC' });
const MONTH_LONG = new Intl.DateTimeFormat('en-IN', { month: 'long', timeZone: 'UTC' });

/** "Tuesday 3 November", with the year when it is not this year's. */
function longDay(d: string, today: string): string {
  const t = new Date(`${d}T00:00:00Z`);
  const year = d.slice(0, 4) === today.slice(0, 4) ? '' : ` ${d.slice(0, 4)}`;
  return `${WEEKDAY_LONG.format(t)} ${t.getUTCDate()} ${MONTH_LONG.format(t)}${year}`;
}

/** "Today", "Tomorrow", "In 5 days", "2 days ago". */
function relativeDay(d: string, today: string): string {
  const n = diffDays(today, d);
  return n === 0 ? 'Today' : n === 1 ? 'Tomorrow' : n === -1 ? 'Yesterday' : n > 0 ? `In ${n} days` : `${-n} days ago`;
}

/** "‹  November 2026  ›" over a calendar. */
function PickerHead({ main, sub, onPrev, onNext, canPrev = true, canNext = true, unit }: {
  main: string; sub?: string; onPrev: () => void; onNext: () => void; canPrev?: boolean; canNext?: boolean; unit: 'month' | 'year';
}) {
  return (
    <div className="picker-head">
      <button type="button" className="picker-arrow" onClick={onPrev} disabled={!canPrev} aria-label={`Previous ${unit}`}><Icon name="left" size={16} sw={2.4} /></button>
      <span className="picker-head-label" aria-live="polite"><b>{main}</b>{sub && <span>{sub}</span>}</span>
      <button type="button" className="picker-arrow" onClick={onNext} disabled={!canNext} aria-label={`Next ${unit}`}><Icon name="right" size={16} sw={2.4} /></button>
    </div>
  );
}

/** A day picker: the chosen day up top, then six full weeks so the card never changes height. */
export function DatePickerSheet({
  isOpen, onClose, value, onChange, today: propToday, min, max, title = 'Select date',
}: {
  isOpen: boolean;
  onClose: () => void;
  value: string;
  onChange: (d: string) => void;
  today?: string;
  min?: string;
  max?: string;
  title?: string;
}) {
  const today = propToday ?? todayIn();
  const picked = value && isIsoDate(value) ? value : '';
  const [viewMonth, setViewMonth] = useState(() => monthOf(picked || today));

  useEffect(() => {
    if (isOpen) setViewMonth(monthOf(picked || today));
  }, [isOpen, picked, today]);

  if (!isOpen) return null;

  const allowed = (d: string) => (!min || d >= min) && (!max || d <= max);
  const pick = (d: string) => { onChange(d); onClose(); };
  const first = `${viewMonth}-01`;
  const weeks = Array.from({ length: 42 }, (_, i) => addDays(first, i - (isoWeekday(first) - 1)));
  const prev = addMonths(viewMonth, -1);
  const next = addMonths(viewMonth, 1);
  const [monthName, year] = formatMonth(viewMonth).split(' ');

  return (
    <Sheet center className="picker" kicker={title} title={picked ? longDay(picked, today) : 'Pick a date'} sub={picked ? relativeDay(picked, today) : undefined} onClose={onClose}>
      <div className="picker-cal">
        <PickerHead
          main={monthName!}
          sub={year}
          unit="month"
          onPrev={() => setViewMonth(prev)}
          onNext={() => setViewMonth(next)}
          canPrev={!min || prev >= monthOf(min)}
          canNext={!max || next <= monthOf(max)}
        />
        <div className="picker-days">
          {WEEK.map(([w], i) => <span key={i} className="picker-wk" aria-hidden="true">{w}</span>)}
          {weeks.map((d) => {
            const cls = ['picker-day', monthOf(d) !== viewMonth && 'out', isoWeekday(d) === 7 && 'sun', d === today && 'today', d === picked && 'sel'].filter(Boolean).join(' ');
            return (
              <button key={d} type="button" className={cls} disabled={!allowed(d)} aria-pressed={d === picked} aria-label={`${formatDay(d)}${d === today ? ', today' : ''}`} onClick={() => pick(d)}>
                <span>{Number(d.slice(8))}</span>
              </button>
            );
          })}
        </div>
      </div>
    </Sheet>
  );
}

export function MonthNav({
  month,
  onChange,
  currentMonth: propCurrentMonth,
}: {
  month: string;
  onChange: (m: string) => void;
  currentMonth?: string;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const currentMonth = propCurrentMonth ?? new Date().toISOString().slice(0, 7);
  const isCurrentMonth = month === currentMonth;
  const isLastMonth = month === addMonths(currentMonth, -1);

  return (
    <>
      <div className="date-nav">
        <button
          type="button"
          className="date-nav-btn"
          onClick={() => onChange(addMonths(month, -1))}
          aria-label="Previous month"
        >
          <Icon name="left" size={18} />
        </button>

        <button
          type="button"
          className="date-nav-center"
          onClick={() => setPickerOpen(true)}
          aria-label="Open month picker"
        >
          <Icon name="calendar" size={16} color="var(--ink-2)" />
          <span className="date-nav-display">
            <span className="date-nav-main">{formatMonth(month)}</span>
          </span>
          {isCurrentMonth && <span className="date-nav-tag today">This month</span>}
          {isLastMonth && <span className="date-nav-tag">Last month</span>}
        </button>

        <button
          type="button"
          className="date-nav-btn"
          onClick={() => onChange(addMonths(month, 1))}
          aria-label="Next month"
        >
          <Icon name="right" size={18} />
        </button>
      </div>

      <MonthPickerSheet
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        value={month}
        onChange={onChange}
        currentMonth={currentMonth}
      />
    </>
  );
}

/** A month picker in the same style: the chosen month up top, then the year's months. */
export function MonthPickerSheet({
  isOpen, onClose, value, onChange, currentMonth: propCurrentMonth, title = 'Select month',
}: {
  isOpen: boolean;
  onClose: () => void;
  value: string;
  onChange: (m: string) => void;
  currentMonth?: string;
  title?: string;
}) {
  const currentMonth = propCurrentMonth ?? new Date().toISOString().slice(0, 7);
  const lastMonth = addMonths(currentMonth, -1);
  const picked = /^\d{4}-(0[1-9]|1[0-2])$/.test(value) ? value : '';
  const [viewYear, setViewYear] = useState(() => Number((picked || currentMonth).slice(0, 4)));

  useEffect(() => {
    if (isOpen) setViewYear(Number((picked || currentMonth).slice(0, 4)));
  }, [isOpen, picked, currentMonth]);

  if (!isOpen) return null;

  const pick = (m: string) => { onChange(m); onClose(); };
  const relative = picked === currentMonth ? 'This month' : picked === lastMonth ? 'Last month' : undefined;

  return (
    <Sheet center className="picker" kicker={title} title={picked ? formatMonth(picked) : 'Pick a month'} sub={relative} onClose={onClose}>
      <div className="picker-cal">
        <PickerHead main={String(viewYear)} unit="year" onPrev={() => setViewYear((y) => y - 1)} onNext={() => setViewYear((y) => y + 1)} />
        <div className="picker-months">
          {MONTH_SHORT.map((name, i) => {
            const m = `${viewYear}-${String(i + 1).padStart(2, '0')}`;
            const cls = ['picker-month', m === currentMonth && 'today', m === picked && 'sel'].filter(Boolean).join(' ');
            return <button key={m} type="button" className={cls} aria-pressed={m === picked} aria-label={formatMonth(m)} onClick={() => pick(m)}>{name}</button>;
          })}
        </div>
      </div>
    </Sheet>
  );
}
